/**
 * ThroughputChart — tokens per second under the request matrix (TRA-1410):
 * how fast the model wrote once a streamed response had started, for each
 * bucket of the window the matrix above shows. Generation slows as a replica
 * takes on more concurrent requests, so reading this against the matrix says
 * whether a slow request waited on load or on its own length.
 *
 * The average is the ink line and the number the "Tokens / sec" tile
 * reports. p50 and p10 sit beside it in the two brand tones, told apart by
 * dash and never by hue (design-principles §3). The hover readout lists
 * every percentile the gateway sends, slow tail first.
 *
 * Renders nothing for a window with no streamed request. The tile already
 * says "none streamed" and why, and a model whose callers never stream
 * would otherwise carry an empty box on its page for good.
 */

import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import type { ThroughputBucket } from '@/components/models/home/contracts'
import { formatRate } from '@/components/models/home/contracts'
import { BucketAxisLabels, ReadoutRow } from '@/components/models/home/RequestsPanel'

/** The request matrix's axis gutter (its w-9 ticks plus gap-3), so a bucket
 *  sits at the same x in both charts. */
const AXIS_WIDTH = 48
const AXIS_GAP = 12
const AXIS_TICK = { fontSize: 10, fill: 'hsl(var(--muted-foreground))' }

type LineKey = 'avg' | 'p50' | 'p10'

/** Drawn in this order, so the average lands on top. */
const LINES: { key: LineKey; label: string; stroke: string; dash: string | undefined }[] = [
  { key: 'p10', label: 'p10', stroke: 'hsl(var(--secondary))', dash: '1 3' },
  { key: 'p50', label: 'p50', stroke: 'hsl(var(--gunmetal-blue))', dash: '4 3' },
  { key: 'avg', label: 'Avg', stroke: 'hsl(var(--foreground))', dash: undefined },
]

/** The readout's percentile rows, slowest first. Each names its own field. */
const PERCENTILES = ['p1', 'p5', 'p10', 'p50', 'p75'] as const

function rate(value: number | null): string {
  return value === null ? '—' : formatRate(value)
}

/** A legend swatch: a short line in the series' stroke and dash. */
export function LineMark({ stroke, dash }: { stroke: string; dash: string | undefined }): React.JSX.Element {
  return (
    <svg width="16" height="2" aria-hidden>
      <line x1="0" y1="1" x2="16" y2="1" stroke={stroke} strokeWidth={2} strokeDasharray={dash} />
    </svg>
  )
}

function ThroughputReadout({
  active,
  payload,
}: {
  active?: boolean
  payload?: readonly { payload?: unknown }[]
}): React.JSX.Element | null {
  const bucket = payload?.[0]?.payload as ThroughputBucket | undefined
  if (!active || !bucket) return null
  return (
    // The request matrix's readout width: room for a week bucket's
    // "Mon 03 · 4p-12a" beside the unit on one line.
    <div className="w-56 border border-border bg-card p-3">
      <p className="flex items-baseline justify-between gap-2 whitespace-nowrap font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
        <span className="text-foreground">{bucket.detail}</span>
        <span>tokens / sec</span>
      </p>
      <dl className="mt-2.5 space-y-1 text-xs">
        {PERCENTILES.map((percentile) => (
          <ReadoutRow key={percentile} label={percentile} value={rate(bucket[percentile])} />
        ))}
        <ReadoutRow label="Avg" value={rate(bucket.avg)} divider />
      </dl>
    </div>
  )
}

/** Names the three lines, the average first since it is the tile's number. */
function Legend(): React.JSX.Element {
  return (
    <ul className="flex items-center gap-x-4">
      {[...LINES].reverse().map((line) => (
        <li
          key={line.key}
          className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground"
        >
          <LineMark stroke={line.stroke} dash={line.dash} />
          {line.label}
        </li>
      ))}
    </ul>
  )
}

/**
 * The lines over the matrix's columns. A numeric axis with half a bucket of
 * room at each end puts bucket i at the centre of the matrix's i-th column.
 */
function Plot({ buckets }: { buckets: ThroughputBucket[] }): React.JSX.Element {
  const rows = buckets.map((bucket, index) => ({ ...bucket, index }))
  return (
    <ResponsiveContainer width="100%" height="100%" minWidth={0}>
      {/* The vertical margins hold the halves of the top and bottom tick
          labels that overhang the plot. */}
      <LineChart data={rows} margin={{ top: 6, right: 0, bottom: 6, left: 0 }}>
        <CartesianGrid stroke="hsl(var(--border) / 0.6)" vertical={false} />
        <XAxis dataKey="index" type="number" domain={[-0.5, buckets.length - 0.5]} hide />
        <YAxis
          domain={[0, 'auto']}
          tickCount={4}
          // Every tick, the 0 included: the default drops a label that
          // touches the plot's edge.
          interval={0}
          allowDecimals={false}
          tickLine={false}
          axisLine={false}
          width={AXIS_WIDTH}
          tickMargin={AXIS_GAP}
          tick={AXIS_TICK}
        />
        <Tooltip
          content={({ active, payload }) => (
            <ThroughputReadout active={active} payload={payload} />
          )}
          cursor={{ stroke: 'hsl(var(--muted-foreground) / 0.4)', strokeDasharray: '3 3' }}
        />
        {LINES.map((line) => (
          <Line
            key={line.key}
            dataKey={line.key}
            stroke={line.stroke}
            strokeWidth={1.5}
            strokeDasharray={line.dash}
            // A bucket with streamed requests between two without has no
            // segment to draw; the dot keeps it on the chart.
            dot={{ r: 1.5, fill: line.stroke, stroke: 'none' }}
            isAnimationActive={false}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  )
}

export function ThroughputChart({
  buckets,
}: {
  buckets: ThroughputBucket[]
}): React.JSX.Element | null {
  const averages = buckets.flatMap((bucket) => (bucket.avg === null ? [] : [bucket.avg]))
  if (averages.length === 0) return null
  // The lines are drawn, not read out, so the label carries their range.
  const summary = `Average tokens per second ranged from ${formatRate(
    Math.min(...averages),
  )} to ${formatRate(Math.max(...averages))} across this window's buckets.`
  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-wider text-foreground">
            Tokens / sec
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            See how fast streamed responses were written after their first
            token. Percentiles are weighted by output tokens.
          </p>
        </div>
        <Legend />
      </div>
      <div className="mt-4 h-36 w-full" role="img" aria-label={summary}>
        <Plot buckets={buckets} />
      </div>
      <BucketAxisLabels labels={buckets.map((bucket) => bucket.label)} />
    </div>
  )
}
